import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/apiClient";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSite, setCurrentSiteState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tyreops_site")); } catch { return null; }
  });
  const [customers, setCustomers] = useState([]);
  const [currentCustomer, setCurrentCustomerState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tyreops_customer")); } catch { return null; }
  });

  const setCurrentSite = (site) => {
    setCurrentSiteState(site);
    if (site) localStorage.setItem("tyreops_site", JSON.stringify(site));
    else localStorage.removeItem("tyreops_site");
  };

  const setCurrentCustomer = (c) => {
    setCurrentCustomerState(c);
    setCurrentSiteState(null);
    localStorage.removeItem("tyreops_site");
    if (c) { localStorage.setItem("tyreops_customer", JSON.stringify(c)); localStorage.setItem("tyreops_customer_id", c.id); }
    else { localStorage.removeItem("tyreops_customer"); localStorage.removeItem("tyreops_customer_id"); }
  };

  const loadCustomers = useCallback(async ({ autoSelect = true } = {}) => {
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
      setCurrentCustomerState((prev) => {
        let next = prev && data.find((c) => c.id === prev.id) ? prev : null;
        if (!next && autoSelect && data.length === 1) next = data[0];
        if (next) { localStorage.setItem("tyreops_customer", JSON.stringify(next)); localStorage.setItem("tyreops_customer_id", next.id); }
        else { localStorage.removeItem("tyreops_customer"); localStorage.removeItem("tyreops_customer_id"); }
        return next;
      });
    } catch (e) { console.error("Failed to load customers", e); }
  }, []);

  const loadMeta = useCallback(async () => {
    try { const { data } = await api.get("/meta"); setMeta(data); } catch (e) { console.error("Failed to load meta", e); }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadMeta();
      loadCustomers();
    } catch (e) { setUser(null); }
    finally { setLoading(false); }
  }, [loadMeta, loadCustomers]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("tyreops_token", data.access_token);
    setUser(data.user);
    setCurrentCustomer(null); // always prompt for customer selection on a fresh login
    loadMeta();
    loadCustomers({ autoSelect: false });
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed", e); }
    localStorage.removeItem("tyreops_token");
    setUser(null);
    window.location.href = "/login";
  };

  const hasRole = (min) => {
    const levels = { technician: 1, admin: 2 };
    return (levels[user?.role] || 0) >= (levels[min] || 0);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, meta, loadMeta, loading, login, logout, hasRole, currentSite, setCurrentSite, customers, currentCustomer, setCurrentCustomer, loadCustomers }}>
      {children}
    </AuthContext.Provider>
  );
}
